import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { Notice } from "../../../../design/components";
import { ApiError } from "../../../../api/client";
import { createEntity, useCrLookup } from "../onboardApi";
import {
  FAMILIES,
  VATIN_PATTERN,
  codeFor,
  codeWithCr,
  participantFor,
  useOnboardDraft,
} from "../useOnboardDraft";
import type { DeployMode, Family } from "../../../../api/types";
import styles from "../onboard.module.css";

/* Brand names (ERPNext, SAP, Autoline) are proper nouns and stay as-is in both languages;
   only "Other" is translated below at the render site. */
const ERP_TYPES = [
  ["erpnext", "ERPNext"],
  ["sap", "SAP"],
  ["autoline", "Autoline"],
  ["other", "Other"],
] as const;

const VATIN_HINT = "The VATIN is OM followed by ten digits.";
const CR_UNCHECKED = "The commercial registration could not be checked. Try again before continuing.";
const LOOKUP_DEBOUNCE_MS = 250;
const FAMILY_NOTE = "At least one family must stay ticked.";

/** The CR lookup runs on a settled value, not on every keystroke. */
function useDebounced(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function StepIdentity({ onCreated }: { onCreated(entityId: string): void }) {
  const { t } = useTranslation();
  const identity = useOnboardDraft((draft) => draft.identity);
  const setIdentity = useOnboardDraft((draft) => draft.setIdentity);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cr = identity.crNumber.trim();
  const settledCr = useDebounced(cr, LOOKUP_DEBOUNCE_MS);
  const lookup = useCrLookup(settledCr);
  const duplicate = lookup.data?.[0];
  // An unfinished or failed lookup is not a clean CR: the column is unique, so a blind
  // POST is a 409 the operator cannot read.
  const crChecked = settledCr === cr && lookup.isSuccess && !lookup.isFetching;
  const vatinBad = identity.vatin.trim() !== "" && !VATIN_PATTERN.test(identity.vatin.trim());
  const ready =
    identity.legalName.trim() !== "" &&
    cr !== "" &&
    !vatinBad &&
    crChecked &&
    duplicate === undefined;

  async function submit(): Promise<void> {
    setSaving(true);
    setError("");
    try {
      const entity = await create(codeFor(identity.legalName, cr));
      onCreated(entity.id);
    } catch (exc) {
      setError(
        exc instanceof ApiError ? exc.message : t("onboard.identity.createError", "The company could not be created."),
      );
    } finally {
      setSaving(false);
    }
  }

  /** Two companies can carry the same name; the second one's code takes its CR. */
  async function create(code: string) {
    try {
      return await post(code);
    } catch (exc) {
      if (exc instanceof ApiError && exc.status === 409 && code !== codeWithCr(code, cr)) {
        return post(codeWithCr(code, cr));
      }
      throw exc;
    }
  }

  function post(code: string) {
    return createEntity({
      code,
      legalName: identity.legalName.trim(),
      crNumber: identity.crNumber.trim(),
      vatin: identity.vatin.trim() || null,
      erpType: identity.erpType,
      erpVersion: identity.erpVersion.trim() || null,
      wave: identity.wave.trim() || null,
      enabledDocTypes: identity.enabledDocTypes,
      deployMode: identity.deployMode,
      // Provisional until step 2 PATCHes the chosen method (M2 ruling 22).
      connectionMethod: "direct_api",
    });
  }

  /** A company always sends something, so the last ticked family cannot be removed. */
  function toggleFamily(family: Family, on: boolean): void {
    if (!on && identity.enabledDocTypes.length === 1) return;
    const next = on
      ? [...identity.enabledDocTypes, family]
      : identity.enabledDocTypes.filter((item) => item !== family);
    setIdentity({ enabledDocTypes: next });
  }

  const lastFamily = identity.enabledDocTypes.length === 1 ? identity.enabledDocTypes[0] : null;

  return (
    <section className={styles.panel}>
      <h2>{t("onboard.identity.title", "Company identity")}</h2>
      <p className={styles.caption}>
        {t(
          "onboard.identity.caption",
          "This company's own identity. The VAT identifier below can be shared across the group.",
        )}
      </p>

      <div className={styles.fields}>
        <div className={`${styles.field} ${styles.wide}`}>
          <label htmlFor="ob-legal-name">{t("onboard.identity.legalName", "Legal name")}</label>
          <input
            id="ob-legal-name"
            className="input"
            value={identity.legalName}
            onChange={(e) => setIdentity({ legalName: e.target.value })}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-cr">{t("onboard.identity.cr", "Commercial registration (CR)")}</label>
          <input
            id="ob-cr"
            className="input"
            inputMode="numeric"
            value={identity.crNumber}
            onChange={(e) => setIdentity({ crNumber: e.target.value })}
          />
          {duplicate ? (
            <span className={styles.error}>
              {t("onboard.identity.crDuplicate", "CR {{cr}} is already used by {{legalName}}.", {
                cr,
                legalName: duplicate.legalName,
              })}
            </span>
          ) : null}
          {lookup.isError ? <span className={styles.error}>{t("onboard.identity.crUnchecked", CR_UNCHECKED)}</span> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-participant">{t("onboard.identity.peppolParticipant", "Peppol participant")}</label>
          <input
            id="ob-participant"
            className={`input ${styles.readonly}`}
            readOnly
            value={participantFor(identity.crNumber)}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-vatin">{t("onboard.identity.vatin", "VAT identification number (VATIN)")}</label>
          <input
            id="ob-vatin"
            className="input"
            value={identity.vatin}
            onChange={(e) => setIdentity({ vatin: e.target.value })}
          />
          {vatinBad ? <span className={styles.error}>{t("onboard.identity.vatinHint", VATIN_HINT)}</span> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-erp-type">{t("onboard.identity.erpType", "ERP type")}</label>
          <select
            id="ob-erp-type"
            className="select"
            value={identity.erpType}
            onChange={(e) => setIdentity({ erpType: e.target.value })}
          >
            {ERP_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {value === "other" ? t("onboard.identity.erpTypeOther", "Other") : label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-erp-version">{t("onboard.identity.erpVersion", "ERP version")}</label>
          <input
            id="ob-erp-version"
            className="input"
            value={identity.erpVersion}
            onChange={(e) => setIdentity({ erpVersion: e.target.value })}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-deploy-mode">{t("onboard.identity.deployMode", "Deploy mode")}</label>
          <select
            id="ob-deploy-mode"
            className="select"
            value={identity.deployMode}
            onChange={(e) => setIdentity({ deployMode: e.target.value as DeployMode })}
          >
            <option value="hub">{t("onboard.identity.deployModeHub", "Shared hub")}</option>
            <option value="dedicated">{t("onboard.identity.deployModeDedicated", "Dedicated")}</option>
          </select>
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-wave">{t("onboard.identity.wave", "Wave")}</label>
          <input
            id="ob-wave"
            className="input"
            value={identity.wave}
            onChange={(e) => setIdentity({ wave: e.target.value })}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor="ob-silence">{t("onboard.identity.silenceThreshold", "Silence threshold (minutes)")}</label>
          <input
            id="ob-silence"
            className="input"
            type="number"
            min={1}
            value={identity.silenceThresholdMinutes}
            onChange={(e) => setIdentity({ silenceThresholdMinutes: Number(e.target.value) })}
          />
        </div>

        <fieldset className={`${styles.field} ${styles.wide}`}>
          <legend>{t("onboard.identity.documentFamilies", "Document families")}</legend>
          <p className={styles.caption}>
            {t("onboard.identity.documentFamiliesCaption", "Which documents does this company send through the hub?")}
          </p>
          <div className={styles.families}>
            {FAMILIES.map((spec) => (
              <div key={spec.key} className={styles.family}>
                <label>
                  <input
                    type="checkbox"
                    checked={identity.enabledDocTypes.includes(spec.key)}
                    disabled={lastFamily === spec.key}
                    aria-describedby={lastFamily === spec.key ? "ob-family-note" : undefined}
                    onChange={(e) => toggleFamily(spec.key, e.target.checked)}
                  />
                  {spec.label}
                </label>
                {spec.helper ? <span className={styles.caption}>{spec.helper}</span> : null}
              </div>
            ))}
          </div>
          {lastFamily ? (
            <span id="ob-family-note" className={styles.caption}>
              {t("onboard.identity.familyNote", FAMILY_NOTE)}
            </span>
          ) : null}
        </fieldset>
      </div>

      {error ? <Notice kind="fail">{error}</Notice> : null}

      <div className={styles.foot}>
        <button type="button" className="btn btn-primary" disabled={!ready || saving} onClick={() => void submit()}>
          {t("onboard.identity.continue", "Continue")}
        </button>
      </div>
    </section>
  );
}
